import { colourSolid, colourTint } from '@/features/common/ui';
import { useIsPhone } from '@/hooks/useIsPhone';
import { cn } from '@/lib/cn';
import { LetterheadBlock } from './LetterheadBlock';
import {
  contactLine,
  hasText,
  isProforma,
  moneyText,
  percentText,
  quantityText,
  showsHsnSac,
  taxColumns,
  type BaseValue,
  type DocumentJson,
  type DocumentLine,
  type DocumentParty,
  type LineTaxCell,
  type MetaRow,
  type MoneyCell,
  type TaxColumns,
} from './document-layout';

/**
 * The document, as a customer receives it.
 *
 * ── WHAT THIS IS ──
 * A rendering of the server's document view - never of the invoice record.
 * The server decides what the letterhead says, whether the title is TAX
 * INVOICE or INVOICE, which tax columns the table has, how every amount is
 * written and what the total is in words; this lays it out in the shape a
 * Zoho invoice has, because that is the shape the owner's customers already
 * know how to read. Nothing here is computed or formatted, so the page and
 * the PDF cannot disagree - `<Money>` is deliberately not used, because it
 * would be a second formatter of the same figure.
 *
 * ── PAPER ON A LAPTOP, CARDS ON A PHONE ──
 * On a wide screen it is an A4 sheet: 210mm wide, at least 297mm tall, with
 * the 12mm margins the print stylesheet uses, so what is on screen is what
 * comes out of the printer. On a phone an A4 page is unreadable and a
 * nine-column table is a horizontal scroll, so the lines become cards and
 * the letterhead stacks - one layout, chosen by `useIsPhone`, for the reason
 * that hook gives: rendering both and hiding one puts every line in the DOM
 * twice.
 *
 * Print: the sheet loses its border, padding and minimum height (the paper
 * supplies those), rows do not break across pages, and the shell is already
 * gone - see `.document` in globals.css.
 */
export function DocumentView({
  document: doc,
  bookName,
  accent,
  showLogo = true,
}: {
  document: DocumentJson;
  /** Stands in for the letterhead's name when the server sent none. */
  bookName: string;
  /** A palette slot for the title, the rule and the table head. The server's answer wins when it sent one. */
  accent?: string | null;
  /** "Show logo" in Settings, honoured on screen as well as in the PDF. */
  showLogo?: boolean;
}) {
  const isPhone = useIsPhone();
  const proforma = isProforma(doc);
  const title = doc.title?.trim() || (proforma ? 'PROFORMA INVOICE' : 'INVOICE');
  const letterhead = doc.letterhead ?? {};
  const totals = doc.totals ?? {};
  const lines = doc.lines ?? [];
  const columns = taxColumns(doc);
  const hsn = showsHsnSac(doc);
  const accentSlot = letterhead.accentColour ?? accent;
  const payment = (doc.paymentDetails ?? []).filter((row) => hasText(row.value));
  const declarations = (doc.declarations ?? []).filter((line) => hasText(line));
  const signature = doc.signatory?.signatureUrl ?? null;
  const left = (doc.meta?.left ?? []).filter((row) => hasText(row.value));
  const right = (doc.meta?.right ?? []).filter((row) => hasText(row.value));

  return (
    <article
      data-testid="document"
      data-kind={doc.kind ?? undefined}
      className={cn(
        'document mx-auto w-full max-w-[210mm] rounded-lg border border-border bg-card p-4 text-card-foreground shadow-sm',
        'sm:min-h-[297mm] sm:p-[12mm]',
        'print:min-h-0 print:max-w-none print:rounded-none print:border-0 print:p-0 print:shadow-none',
      )}
    >
      <LetterheadBlock
        logoUrl={showLogo ? letterhead.logoUrl : null}
        name={letterhead.name?.trim() || bookName}
        addressLines={letterhead.addressLines ?? []}
        gstin={letterhead.gstin}
        contact={contactLine(letterhead)}
        tagline={letterhead.tagline}
        accent={accentSlot}
        testId="document-letterhead"
      />

      {/* The title on the right, where the eye lands after the letterhead.
          A proforma says so here, in the same words the banner above the
          document uses - the document is what gets forwarded. */}
      <h2
        className="mt-4 text-right text-lg font-bold uppercase tracking-wider sm:text-xl"
        style={{ color: colourSolid(accentSlot) }}
        data-testid="document-kind"
      >
        {title}
      </h2>

      <section className="mt-3 grid gap-x-6 gap-y-2 text-sm sm:grid-cols-2" aria-label="Document details">
        <dl className="grid grid-cols-[auto_1fr] gap-x-4 gap-y-1" data-testid="document-meta">
          {left.map((row, index) => (
            <Meta key={`${row.label}-${index}`} row={row} strong={index === 0} />
          ))}
          {hasText(doc.originally) ? <Meta row={{ label: 'Originally', value: doc.originally?.replace(/^Originally\s+/i, '') }} /> : null}
        </dl>
        {/* `1fr_auto` with the label right-aligned, so "Place of Supply" sits
            beside its value at the right edge rather than across the page from it. */}
        <dl className="grid grid-cols-[auto_1fr] gap-x-4 gap-y-1 sm:grid-cols-[1fr_auto] sm:text-right">
          {right.map((row, index) => (
            <Meta key={`${row.label}-${index}`} row={row} />
          ))}
        </dl>
      </section>

      <section className="mt-5 grid gap-4 sm:grid-cols-2">
        <Party title="Bill To" party={doc.billTo} testId="document-bill-to" />
        {doc.shipTo ? <Party title="Ship To" party={doc.shipTo} testId="document-ship-to" /> : null}
      </section>

      {isPhone ? (
        <LineCards lines={lines} columns={columns} />
      ) : (
        <LinesTable lines={lines} columns={columns} hsn={hsn} accent={accentSlot} />
      )}

      <section className="mt-4 flex flex-col gap-4 sm:flex-row sm:items-start sm:justify-between">
        <div className="min-w-0 space-y-3 text-sm sm:max-w-[55%]">
          {hasText(doc.totalInWords) ? (
            <p data-testid="document-total-in-words">
              <span className="block text-xs font-medium uppercase tracking-wide text-muted-foreground">Total in words</span>
              <span className="font-medium italic">{doc.totalInWords}</span>
            </p>
          ) : null}
          {declarations.length ? (
            <ul className="space-y-0.5 text-xs text-muted-foreground" data-testid="document-declarations">
              {declarations.map((line) => (
                <li key={line}>{line}</li>
              ))}
            </ul>
          ) : null}
        </div>
        <div className="w-full space-y-3 sm:max-w-xs">
          <dl className="w-full space-y-1.5 text-sm" data-testid="document-totals">
            <SumRow label="Sub total" cell={totals.subTotal} />
            {totals.discount?.amount ? <SumRow label={totals.discount.label || 'Discount'} cell={totals.discount.amount} negative muted /> : null}
            {totals.shipping ? <SumRow label="Shipping" cell={totals.shipping} muted /> : null}
            {(totals.taxes ?? []).map((tax, index) => (
              <SumRow
                key={`${tax.label}-${index}`}
                label={`${tax.label ?? 'Tax'}${totals.taxesIncluded ? ' (included)' : ''}`}
                cell={tax.amount}
                muted
              />
            ))}
            {totals.tcs?.amount ? <SumRow label={totals.tcs.label || 'TCS'} cell={totals.tcs.amount} muted /> : null}
            {totals.adjustment?.amount ? <SumRow label={totals.adjustment.label || 'Adjustment'} cell={totals.adjustment.amount} muted /> : null}
            <SumRow label="Total" cell={totals.total} emphasis />
            {/* A proforma owes nothing, so it has no "paid" and no "balance";
                the closing line names what the invoice will ask for. */}
            {proforma ? (
              <SumRow label="Amount payable on invoice" cell={totals.total} strong />
            ) : (
              <>
                {totals.paymentsReceived ? <SumRow label="Payment received" cell={totals.paymentsReceived} negative muted /> : null}
                <SumRow label="Balance Due" cell={totals.balanceDue ?? totals.total} strong testId="document-balance-due" />
              </>
            )}
          </dl>
          {doc.baseValue ? <BaseValuePanel value={doc.baseValue} /> : null}
        </div>
      </section>

      {hasText(doc.notes) ? <TextBlock title="Notes" text={doc.notes} testId="document-notes" /> : null}
      {hasText(doc.terms) ? <TextBlock title="Terms & conditions" text={doc.terms} testId="document-terms" /> : null}

      <section className="mt-6 flex flex-col gap-6 sm:flex-row sm:items-end sm:justify-between">
        {payment.length || doc.upiQr?.pngDataUrl ? (
          <div className="min-w-0 text-sm" data-testid="document-payment-details">
            {payment.length ? (
              <>
                <p className="text-xs font-medium uppercase tracking-wide text-muted-foreground">Payment details</p>
                <dl className="mt-1 grid grid-cols-[auto_1fr] gap-x-3 gap-y-0.5">
                  {payment.map((row, index) => (
                    <Meta key={`${row.label}-${index}`} row={row} />
                  ))}
                </dl>
              </>
            ) : null}
            {/*
              ── SCAN TO PAY, BOTTOM-LEFT ──
              The same square the PDF prints: a UPI intent with the balance
              due and the invoice number filled in. On screen it is also a
              link, so a customer reading the invoice on their phone taps
              instead of scanning their own screen.
            */}
            {doc.upiQr?.pngDataUrl ? (
              <a
                href={doc.upiQr.uri ?? undefined}
                className="mt-3 flex items-center gap-3 no-underline"
                data-testid="document-upi-qr"
                data-tap-exempt="a QR square with its caption; the whole block is the target"
              >
                <img src={doc.upiQr.pngDataUrl} alt="UPI QR code" className="size-24 rounded-sm bg-paper" />
                <span className="min-w-0">
                  <span className="block text-xs font-medium uppercase tracking-wide text-muted-foreground">Scan to pay</span>
                  <span className="block">{doc.upiQr.caption}</span>
                  <span className="block text-xs text-muted-foreground">Any UPI app · amount and invoice number are filled in</span>
                </span>
              </a>
            ) : null}
          </div>
        ) : (
          <span aria-hidden />
        )}
        <div className="flex shrink-0 flex-col items-start text-sm sm:items-end" data-testid="document-signature">
          {signature ? (
            <img src={signature} alt="" className="max-h-14 max-w-[45mm] rounded-sm bg-paper object-contain" data-testid="document-signature-image" />
          ) : (
            <span aria-hidden className="h-14" />
          )}
          {hasText(doc.signatory?.name) ? <p className="mt-1 font-medium">{doc.signatory?.name}</p> : null}
          <p className="border-t border-border pt-1 text-xs text-muted-foreground">{doc.signatory?.label || 'Authorized Signature'}</p>
        </div>
      </section>
    </article>
  );
}

function Meta({ row, strong }: { row: MetaRow; strong?: boolean }) {
  if (!hasText(row.value)) return null;
  return (
    <>
      <dt className="text-muted-foreground">{row.label}</dt>
      <dd className={cn('min-w-0 break-words', strong && 'font-semibold')}>{row.value}</dd>
    </>
  );
}

function Party({ title, party, testId }: { title: string; party: DocumentParty | null | undefined; testId: string }) {
  return (
    <div className="min-w-0 text-sm" data-testid={testId}>
      <p className="text-xs font-medium uppercase tracking-wide text-muted-foreground">{title}</p>
      <p className="mt-1 font-semibold">{party?.name?.trim() || '—'}</p>
      {(party?.lines ?? []).map((line, index) => (
        <p key={`${index}-${line}`} className="break-words">
          {line}
        </p>
      ))}
      {hasText(party?.gstin) ? <p>GSTIN {party?.gstin}</p> : null}
      {hasText(party?.email) ? <p className="break-words text-muted-foreground">{party?.email}</p> : null}
    </div>
  );
}

const HEAD = 'px-2 py-2 text-left text-[11px] font-semibold uppercase tracking-wide';
const CELL = 'px-2 py-2';

/**
 * The lines, on a wide screen. Every column is one the document needs: the
 * HSN/SAC column and the tax columns are the ones the server named.
 */
function LinesTable({ lines, columns, hsn, accent }: { lines: DocumentLine[]; columns: TaxColumns; hsn: boolean; accent?: string | null }) {
  return (
    <div className="mt-5 overflow-x-auto">
      <table className="w-full text-sm" data-testid="document-lines">
        <thead>
          <tr style={{ backgroundColor: colourTint(accent) }}>
            <th scope="col" className={cn(HEAD, 'w-8')}>
              #
            </th>
            <th scope="col" className={HEAD}>
              Item & description
            </th>
            {hsn ? (
              <th scope="col" className={HEAD}>
                HSN/SAC
              </th>
            ) : null}
            <th scope="col" className={cn(HEAD, 'text-right')}>
              Qty
            </th>
            <th scope="col" className={cn(HEAD, 'text-right')}>
              Rate
            </th>
            {columns === 'cgst_sgst' ? (
              <>
                <th scope="col" className={cn(HEAD, 'text-right')}>
                  CGST
                </th>
                <th scope="col" className={cn(HEAD, 'text-right')}>
                  SGST
                </th>
              </>
            ) : null}
            {columns === 'igst' ? (
              <th scope="col" className={cn(HEAD, 'text-right')}>
                IGST
              </th>
            ) : null}
            {columns === 'tax' ? (
              <th scope="col" className={cn(HEAD, 'text-right')}>
                Tax
              </th>
            ) : null}
            <th scope="col" className={cn(HEAD, 'text-right')}>
              Amount
            </th>
          </tr>
        </thead>
        <tbody>
          {lines.map((line, index) => (
            <tr key={`${line.index ?? index}-${line.name}`} className="border-b border-border align-top" data-line>
              <td className={cn(CELL, 'text-muted-foreground')}>{line.index ?? index + 1}</td>
              <td className={cn(CELL, 'min-w-[10rem]')}>
                <span className="font-medium">{line.name}</span>
                {hasText(line.description) ? (
                  <p className="whitespace-pre-wrap text-xs text-muted-foreground">{line.description}</p>
                ) : null}
                {line.discount ? <p className="text-xs text-muted-foreground">Less discount {moneyText(line.discount)}</p> : null}
              </td>
              {hsn ? <td className={cn(CELL, 'tabular')}>{line.hsnSac ?? ''}</td> : null}
              <td className={cn(CELL, 'whitespace-nowrap text-right tabular')}>{quantityText(line)}</td>
              <td className={cn(CELL, 'whitespace-nowrap text-right tabular')}>{moneyText(line.rate)}</td>
              {columns === 'cgst_sgst' ? (
                <>
                  <TaxCell tax={line.taxes?.cgst} />
                  <TaxCell tax={line.taxes?.sgst} />
                </>
              ) : null}
              {columns === 'igst' ? <TaxCell tax={line.taxes?.igst} /> : null}
              {columns === 'tax' ? <OtherTaxCell taxes={line.taxes?.other} /> : null}
              <td className={cn(CELL, 'whitespace-nowrap text-right font-medium tabular')}>{moneyText(line.amount)}</td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

/**
 * "9% / 4,500.00" in one cell: the rate over the amount, as Zoho prints it,
 * so a customer's accountant can check the split without a calculator.
 */
function TaxCell({ tax }: { tax: LineTaxCell | null | undefined }) {
  return (
    <td className={cn(CELL, 'whitespace-nowrap text-right tabular')}>
      {tax?.amount ? (
        <>
          <span className="block text-xs text-muted-foreground">{percentText(tax.percentage)}</span>
          {moneyText(tax.amount)}
        </>
      ) : (
        <span className="text-muted-foreground">—</span>
      )}
    </td>
  );
}

/** A plain named rate outside GST - "VAT 20%" - one per line, stacked when a line carries two. */
function OtherTaxCell({ taxes }: { taxes: Array<LineTaxCell & { name?: string | null }> | null | undefined }) {
  const rows = (taxes ?? []).filter((tax) => tax.amount);
  return (
    <td className={cn(CELL, 'whitespace-nowrap text-right tabular')}>
      {rows.length ? (
        rows.map((tax, index) => (
          <span key={`${tax.name}-${index}`} className="block">
            <span className="block text-xs text-muted-foreground">
              {hasText(tax.name) ? `${tax.name} ` : ''}
              {percentText(tax.percentage)}
            </span>
            {moneyText(tax.amount)}
          </span>
        ))
      ) : (
        <span className="text-muted-foreground">—</span>
      )}
    </td>
  );
}

/** The lines as cards, for a phone. The same facts, stacked. */
function LineCards({ lines, columns }: { lines: DocumentLine[]; columns: TaxColumns }) {
  const taxText = (label: string, tax: LineTaxCell | null | undefined) =>
    tax?.amount ? ` · ${label} ${percentText(tax.percentage)} ${moneyText(tax.amount)}` : '';
  return (
    <ol className="mt-5 space-y-2" data-testid="document-lines">
      {lines.map((line, index) => (
        <li key={`${line.index ?? index}-${line.name}`} className="rounded-md border border-border p-3 text-sm" data-line>
          <div className="flex items-start justify-between gap-3">
            <div className="min-w-0">
              <p className="font-medium">
                <span className="mr-1.5 text-muted-foreground">{line.index ?? index + 1}.</span>
                {line.name}
              </p>
              {hasText(line.description) ? <p className="whitespace-pre-wrap text-xs text-muted-foreground">{line.description}</p> : null}
              {hasText(line.hsnSac) ? <p className="text-xs text-muted-foreground">HSN/SAC {line.hsnSac}</p> : null}
            </div>
            <p className="shrink-0 font-semibold tabular">{moneyText(line.amount)}</p>
          </div>
          <p className="mt-1 text-xs text-muted-foreground tabular">
            {quantityText(line)} × {moneyText(line.rate)}
            {line.discount ? ` · less ${moneyText(line.discount)}` : ''}
            {columns === 'cgst_sgst' ? taxText('CGST', line.taxes?.cgst) + taxText('SGST', line.taxes?.sgst) : ''}
            {columns === 'igst' ? taxText('IGST', line.taxes?.igst) : ''}
            {columns === 'tax' ? taxText(line.taxes?.other?.[0]?.name ?? 'Tax', line.taxes?.other?.[0]) : ''}
          </p>
        </li>
      ))}
    </ol>
  );
}

function SumRow({
  label,
  cell,
  negative,
  muted,
  emphasis,
  strong,
  testId,
}: {
  label: string;
  cell: MoneyCell | null | undefined;
  /** A deduction - discount, payment received - shown with a leading minus. */
  negative?: boolean;
  muted?: boolean;
  emphasis?: boolean;
  /** The closing line - Balance Due - in bold, on a tinted band. */
  strong?: boolean;
  testId?: string;
}) {
  const text = moneyText(cell);
  return (
    <div
      className={cn(
        'flex items-center justify-between gap-4',
        muted && 'text-muted-foreground',
        emphasis && 'border-t border-border pt-1.5 font-semibold',
        strong && 'rounded-md bg-muted px-2 py-1.5 text-base font-bold',
      )}
      data-testid={testId}
    >
      <dt>{label}</dt>
      <dd className="tabular">{negative && text ? `(-) ${text}` : text}</dd>
    </div>
  );
}

/**
 * The rupee value of a foreign-currency document, under the totals.
 *
 * ── WHY A PANEL AND NOT A FOOTNOTE ──
 * It used to be one grey line among the declarations, and the accountant
 * could not file from it: GSTR-1 Table 6A and the FEMA declaration are both
 * in rupees, and the figure they need was the hardest thing on the page to
 * find. It sits in the totals' own column, in the same shape the PDF draws,
 * so the INR total is read straight after the GBP one. The amounts are the
 * server's - stored at issue, never re-converted here.
 */
function BaseValuePanel({ value }: { value: BaseValue }) {
  const rows = (value.rows ?? []).filter((row) => row.amount);
  if (!rows.length) return null;
  return (
    <div className="text-sm" data-testid="document-base-value">
      <p className="mb-1 text-right text-xs font-medium uppercase tracking-wide text-muted-foreground">
        {value.title?.trim() || `Value in ${value.currency ?? 'base currency'}`}
      </p>
      <dl className="space-y-0.5 rounded-md border border-border bg-muted/50 px-2 py-1.5">
        {rows.map((row, index) => (
          <div
            key={`${row.label}-${index}`}
            className={cn('flex items-center justify-between gap-4', index === rows.length - 1 && 'font-semibold')}
          >
            <dt>{row.label}</dt>
            <dd className="tabular">{moneyText(row.amount)}</dd>
          </div>
        ))}
      </dl>
      {hasText(value.rate) ? <p className="mt-1 text-right text-xs text-muted-foreground">{value.rate}</p> : null}
      {hasText(value.note) ? <p className="text-right text-xs text-muted-foreground">{value.note}</p> : null}
    </div>
  );
}

function TextBlock({ title, text, testId }: { title: string; text: string | null | undefined; testId: string }) {
  return (
    <section className="mt-5 text-sm" data-testid={testId}>
      <p className="text-xs font-medium uppercase tracking-wide text-muted-foreground">{title}</p>
      <p className="mt-1 whitespace-pre-wrap break-words">{text}</p>
    </section>
  );
}
